/**
 * Closed enum of lookup keys for the `hl7-config` mapping table. Each key
 * carries its own result shape (see `@agilemd/types`):
 *   - scalar config (TIMEZONE) returns `Hl7ConfigValueResult`
 *   - identifier kinds (PATIENT_MRN, ENCOUNTER_CSN, etc.) return `IdentifierKindResult`
 */
export const HL7_CONFIG_LOOKUP_KEYS = {
  TIMEZONE: 'timezone',
  PATIENT_MRN: 'patient-identifier:mrn',
  PATIENT_PRIOR_MRN: 'patient-identifier:prior-mrn',
  PATIENT_ENTERPRISE_ID: 'patient-identifier:enterprise-id',
  ENCOUNTER_CSN: 'encounter-identifier:csn',
  ENCOUNTER_VISIT_NUMBER: 'encounter-identifier:visit-number',
  ENCOUNTER_FIN: 'encounter-identifier:fin',
  ENCOUNTER_PRIOR_CSN: 'encounter-identifier:prior-csn',
  LOCATION_TYPE_CUSTOM: 'location-type:custom',
  MERGE_TRIGGER_EVENTS: 'merge:trigger-events',
  ENCOUNTER_MERGE_TRIGGER_EVENTS: 'encounter-merge:trigger-events',
  ENCOUNTER_MOVE_TRIGGER_EVENTS: 'encounter-move:trigger-events'
} as const;

/** Editor grouping: keys sharing a category render together in the Admin Console. */
export const HL7_CONFIG_KEY_CATEGORIES = {
  GENERAL: 'general',
  PATIENT_IDENTIFIERS: 'patient-identifiers',
  ENCOUNTER_IDENTIFIERS: 'encounter-identifiers',
  LOCATIONS: 'locations',
  MERGES: 'merges'
} as const;

export type Hl7ConfigKeyCategory =
  (typeof HL7_CONFIG_KEY_CATEGORIES)[keyof typeof HL7_CONFIG_KEY_CATEGORIES];

/**
 * Publish-to-LIVE needs at least one entry for some key in each group; keys
 * sharing a group are interchangeable (a feed identifies patients by MRN or
 * by enterprise ID).
 */
export const HL7_CONFIG_REQUIREMENT_GROUPS = {
  TIMEZONE: 'timezone',
  PATIENT_IDENTIFIER: 'patient-identifier',
  ENCOUNTER_IDENTIFIER: 'encounter-identifier'
} as const;

export type Hl7ConfigRequirementGroup =
  (typeof HL7_CONFIG_REQUIREMENT_GROUPS)[keyof typeof HL7_CONFIG_REQUIREMENT_GROUPS];

export interface Hl7ConfigKeyMetadata {
  label: string;
  category: Hl7ConfigKeyCategory;
  requirementGroup?: Hl7ConfigRequirementGroup;
}

/**
 * The single source for which `hl7-config` keys exist and how they present.
 * `core-content` joins this with each key's zod result schema; the Admin Console
 * joins it with the per-shape editor wiring. Both derive their key list from
 * here, so a key can no longer reach the runtime while staying invisible in the
 * editor. The `Record` annotation makes an unannotated key a compile error.
 *
 * Declaration order is the order the editor lists keys within a category.
 */
export const HL7_CONFIG_KEY_METADATA: Record<
  (typeof HL7_CONFIG_LOOKUP_KEYS)[keyof typeof HL7_CONFIG_LOOKUP_KEYS],
  Hl7ConfigKeyMetadata
> = {
  [HL7_CONFIG_LOOKUP_KEYS.TIMEZONE]: {
    label: 'Timezone',
    category: HL7_CONFIG_KEY_CATEGORIES.GENERAL,
    requirementGroup: HL7_CONFIG_REQUIREMENT_GROUPS.TIMEZONE
  },
  [HL7_CONFIG_LOOKUP_KEYS.PATIENT_MRN]: {
    label: 'Patient MRN',
    category: HL7_CONFIG_KEY_CATEGORIES.PATIENT_IDENTIFIERS,
    requirementGroup: HL7_CONFIG_REQUIREMENT_GROUPS.PATIENT_IDENTIFIER
  },
  [HL7_CONFIG_LOOKUP_KEYS.PATIENT_PRIOR_MRN]: {
    label: 'Patient Prior MRN (merge)',
    category: HL7_CONFIG_KEY_CATEGORIES.PATIENT_IDENTIFIERS
  },
  [HL7_CONFIG_LOOKUP_KEYS.PATIENT_ENTERPRISE_ID]: {
    label: 'Patient Enterprise ID',
    category: HL7_CONFIG_KEY_CATEGORIES.PATIENT_IDENTIFIERS,
    requirementGroup: HL7_CONFIG_REQUIREMENT_GROUPS.PATIENT_IDENTIFIER
  },
  [HL7_CONFIG_LOOKUP_KEYS.ENCOUNTER_CSN]: {
    label: 'Encounter CSN',
    category: HL7_CONFIG_KEY_CATEGORIES.ENCOUNTER_IDENTIFIERS,
    requirementGroup: HL7_CONFIG_REQUIREMENT_GROUPS.ENCOUNTER_IDENTIFIER
  },
  [HL7_CONFIG_LOOKUP_KEYS.ENCOUNTER_VISIT_NUMBER]: {
    label: 'Encounter Visit Number',
    category: HL7_CONFIG_KEY_CATEGORIES.ENCOUNTER_IDENTIFIERS,
    requirementGroup: HL7_CONFIG_REQUIREMENT_GROUPS.ENCOUNTER_IDENTIFIER
  },
  [HL7_CONFIG_LOOKUP_KEYS.ENCOUNTER_FIN]: {
    label: 'Encounter Account Number / FIN',
    category: HL7_CONFIG_KEY_CATEGORIES.ENCOUNTER_IDENTIFIERS,
    requirementGroup: HL7_CONFIG_REQUIREMENT_GROUPS.ENCOUNTER_IDENTIFIER
  },
  [HL7_CONFIG_LOOKUP_KEYS.ENCOUNTER_PRIOR_CSN]: {
    label: 'Encounter Prior CSN (merge/move)',
    category: HL7_CONFIG_KEY_CATEGORIES.ENCOUNTER_IDENTIFIERS
  },
  [HL7_CONFIG_LOOKUP_KEYS.LOCATION_TYPE_CUSTOM]: {
    label: 'Custom Location Type Source',
    category: HL7_CONFIG_KEY_CATEGORIES.LOCATIONS
  },
  [HL7_CONFIG_LOOKUP_KEYS.MERGE_TRIGGER_EVENTS]: {
    label: 'Patient Merge Trigger Events',
    category: HL7_CONFIG_KEY_CATEGORIES.MERGES
  },
  [HL7_CONFIG_LOOKUP_KEYS.ENCOUNTER_MERGE_TRIGGER_EVENTS]: {
    label: 'Encounter Merge Trigger Events',
    category: HL7_CONFIG_KEY_CATEGORIES.MERGES
  },
  [HL7_CONFIG_LOOKUP_KEYS.ENCOUNTER_MOVE_TRIGGER_EVENTS]: {
    label: 'Encounter Move Trigger Events',
    category: HL7_CONFIG_KEY_CATEGORIES.MERGES
  }
};

/**
 * ADT trigger events the platform supports as patient-merge triggers. The
 * `merge:trigger-events` hl7-config key selects which of these an org has
 * enabled; hl7-processor registers a merge handler per supported trigger.
 * Extend this (plus the merge handler) before offering new events in config.
 */
export const HL7_MERGE_TRIGGER_EVENTS = {
  A40: 'A40',
  A18: 'A18'
} as const;

/**
 * ADT trigger events supported as visit (Encounter) merge triggers. The
 * `encounter-merge:trigger-events` hl7-config key selects which of these an
 * org has enabled; empty/absent = encounter merges disabled (dark default).
 * A41 is the account-grain event; vault has no Account resource (FIN and CSN
 * are both `Encounter.identifier`), so it runs the same visit merge — feeds
 * that carry the prior account in MRG-3 point the prior-CSN rule there.
 */
export const HL7_ENCOUNTER_MERGE_TRIGGER_EVENTS = {
  A42: 'A42',
  A41: 'A41'
} as const;

/**
 * ADT trigger events supported as visit (Encounter) move triggers. The
 * `encounter-move:trigger-events` hl7-config key selects which of these an
 * org has enabled; empty/absent = encounter moves disabled (dark default).
 * A44 is the account-grain event; vault has no Account resource (FIN and CSN
 * are both `Encounter.identifier`), so it runs the same visit move.
 */
export const HL7_ENCOUNTER_MOVE_TRIGGER_EVENTS = {
  A45: 'A45',
  A44: 'A44'
} as const;

/**
 * Known HL7 sending systems — the normalized vocabulary of the
 * `dim_ehr_vendor` mapping-table dimension. Derived from MSH-3 (Sending
 * Application) by hl7-processor's `normalizeVendor`: known spellings
 * collapse to these keys, anything else passes through lowercased. The
 * list deliberately includes lab systems (LIS) alongside EHRs — a lab ORU
 * feed identifies as its LIS, and rows scoped to it match only that feed.
 *
 * Open-ended: the admin console offers these as picker options plus a
 * free-text escape hatch, and the platform stores any string.
 */
export const HL7_SENDING_SYSTEMS = {
  EPIC: 'epic',
  EPIC_KIT: 'epic-kit',
  CERNER: 'cerner',
  SUNQUEST: 'sunquest',
  BEAKER: 'beaker'
} as const;
export type Hl7SendingSystem = (typeof HL7_SENDING_SYSTEMS)[keyof typeof HL7_SENDING_SYSTEMS];

export const HL7_SENDING_SYSTEM_LABELS: Record<Hl7SendingSystem, string> = {
  [HL7_SENDING_SYSTEMS.EPIC]: 'Epic',
  [HL7_SENDING_SYSTEMS.EPIC_KIT]: 'Epic Kit',
  [HL7_SENDING_SYSTEMS.CERNER]: 'Cerner',
  [HL7_SENDING_SYSTEMS.SUNQUEST]: 'Sunquest (lab)',
  [HL7_SENDING_SYSTEMS.BEAKER]: 'Beaker (Epic lab)'
};

/**
 * HL7 CX-datatype component positions read for each identifier-gate
 * discriminator. The CX layout is shared by every identifier-kind source
 * field (PID-3, MRG-1, PV1-19, PID-18). Single source of truth for
 * hl7-processor's gate evaluation and the admin-console gate editor's
 * field labels (e.g. `PID-3.5`).
 */
export const IDENTIFIER_GATE_COMPONENTS = {
  'id-type-code': { component: 5 },
  'assigning-authority': { component: 4, subcomponent: 1 },
  'assigning-facility': { component: 6, subcomponent: 1 }
} as const;
